import { bindEvent, type PusherChannel } from '@nexo/api';
import {
  realtimeChannels,
  type ActiveCall,
  type Channel,
  type CustomRole,
  type Team,
  type TeamMember,
} from '@nexo/types';
import { useToast } from '@nexo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMe } from '../features/auth/AuthProvider';
import { callStore } from '../features/calls/callStore';
import { recordingStore } from '../features/calls/summaryRecorder';
import { channelLabel } from '../features/channels/ChannelSidebar';
import { notify, playSound } from '../features/notifications/alerts';
import { mentionsMe } from '../features/notifications/channelNotify';
import { alertForMessage } from '../features/notifications/messageAlert';
import { api } from '../lib/api';
import { cache } from '../lib/cache';
import { t } from '../lib/i18n';
import { keys } from '../lib/queries';
import { useRealtime } from './RealtimeProvider';
import { typingStore } from './typing';

interface PresenceMembers {
  each: (fn: (member: { id: string }) => void) => void;
}

/**
 * Subscribes to everything the team view needs: team events, presence and every conversation
 * the user belongs to (so unread counters stay live). Returns the set of online user ids, or
 * null when presence is not available.
 */
export function useTeamRealtime(
  teamId: string,
  channels: Channel[] | undefined,
): Set<string> | null {
  const { client, activeChannelRef } = useRealtime();
  const me = useMe();
  const queryClient = useQueryClient();
  const [online, setOnline] = useState<Set<string> | null>(null);
  const markReadTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const toast = useToast();
  const navigate = useNavigate();
  // Latest conversations for event handlers without resubscribing on every change.
  const channelsRef = useRef(channels);
  useEffect(() => {
    channelsRef.current = channels;
  }, [channels]);

  // Team channel + presence.
  useEffect(() => {
    if (!client) return;
    const team = client.subscribe(realtimeChannels.team(teamId));
    const unbind = [
      bindEvent(team, 'task:created', (task) => cache.upsertTask(queryClient, task)),
      bindEvent(team, 'task:updated', (task) => cache.upsertTask(queryClient, task)),
      bindEvent(team, 'task:deleted', ({ id }) => cache.removeTask(queryClient, teamId, id)),
      bindEvent(team, 'tasks:changed', () => {
        void queryClient.invalidateQueries({ queryKey: keys.tasks(teamId) });
      }),
      bindEvent(team, 'columns:changed', () => {
        void queryClient.invalidateQueries({ queryKey: keys.columns(teamId) });
      }),
      bindEvent(team, 'sprints:changed', () => {
        void queryClient.invalidateQueries({ queryKey: keys.sprints(teamId) });
      }),
      bindEvent(team, 'team:updated', () => {
        void queryClient.invalidateQueries({ queryKey: keys.teams });
      }),
      bindEvent(team, 'team:deleted', () => {
        queryClient.setQueryData<Team[]>(keys.teams, (list) =>
          list?.filter((other) => other.id !== teamId),
        );
      }),
      bindEvent(team, 'channel:created', (channel) => cache.upsertChannel(queryClient, channel)),
      bindEvent(team, 'member:joined', ({ member }) => {
        queryClient.setQueryData<TeamMember[]>(keys.members(teamId), (list) =>
          list && !list.some((m) => m.id === member.id) ? [...list, member] : list,
        );
        void queryClient.invalidateQueries({ queryKey: keys.teams });
      }),
      bindEvent(team, 'member:updated', ({ member }) => {
        queryClient.setQueryData<TeamMember[]>(keys.members(teamId), (list) =>
          list?.map((m) => (m.id === member.id ? member : m)),
        );
        // My own permission role lives on the team object.
        if (member.id === me.id) void queryClient.invalidateQueries({ queryKey: keys.teams });
      }),
      bindEvent(team, 'member:removed', ({ userId }) => {
        queryClient.setQueryData<TeamMember[]>(keys.members(teamId), (list) =>
          list?.filter((m) => m.id !== userId),
        );
        void queryClient.invalidateQueries({ queryKey: keys.roles(teamId) });
      }),
      bindEvent(team, 'roles:changed', () => {
        void queryClient.invalidateQueries({ queryKey: keys.roles(teamId) });
        void queryClient.invalidateQueries({ queryKey: keys.members(teamId) });
        // My permissions live on the team object.
        void queryClient.invalidateQueries({ queryKey: keys.teams });
      }),
      // What I can see and do in each channel depends on my permissions: refetch my own list.
      bindEvent(team, 'channels:changed', () => {
        void queryClient.invalidateQueries({ queryKey: keys.channels(teamId) });
        void queryClient.invalidateQueries({ queryKey: keys.teams });
      }),
    ];

    const presenceName = realtimeChannels.presence(teamId);
    const presence = client.subscribe(presenceName);
    presence.bind('pusher:subscription_succeeded', (members: PresenceMembers) => {
      const ids = new Set<string>();
      members.each((member) => ids.add(member.id));
      setOnline(ids);
    });
    presence.bind('pusher:member_added', ({ id }: { id: string }) =>
      setOnline((current) => new Set(current).add(id)),
    );
    presence.bind('pusher:member_removed', ({ id }: { id: string }) =>
      setOnline((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      }),
    );
    // Presence requires a paid EventSocket plan; degrade gracefully without it.
    presence.bind('pusher:subscription_error', () => setOnline(null));

    return () => {
      unbind.forEach((fn) => fn());
      client.unsubscribe(realtimeChannels.team(teamId));
      client.unsubscribe(presenceName);
      setOnline(null);
    };
  }, [client, teamId, queryClient, me.id]);

  // One subscription per conversation.
  const channelIds = (channels ?? [])
    .map((c) => c.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (!client || !channelIds) return;
    const subscriptions: { name: string; channel: PusherChannel; unbind: (() => void)[] }[] = [];

    for (const channelId of channelIds.split(',')) {
      const name = realtimeChannels.channel(channelId);
      const channel = client.subscribe(name);
      subscriptions.push({
        name,
        channel,
        unbind: [
          bindEvent(channel, 'message:created', (message) => {
            cache.upsertMessage(queryClient, message);
            typingStore.clear(message.channelId, message.author.id);
            // Thread replies: the thread's own notification covers the people involved.
            if (message.parentId) return;
            if (message.attachments.length > 0) {
              void queryClient.invalidateQueries({ queryKey: keys.files(message.channelId) });
            }
            if (message.author.id === me.id) return;
            const visible =
              activeChannelRef.current === message.channelId &&
              document.visibilityState === 'visible';
            if (visible) {
              clearTimeout(markReadTimer.current);
              markReadTimer.current = setTimeout(
                () => void api.channels.markRead(message.channelId),
                800,
              );
            }
            const conversation = channelsRef.current?.find((c) => c.id === message.channelId);
            const members = queryClient.getQueryData<TeamMember[]>(keys.members(teamId)) ?? [];
            const roles = queryClient.getQueryData<CustomRole[]>(keys.roles(teamId)) ?? [];
            const mention = mentionsMe(message, conversation, { id: me.id, members, roles });
            cache.patchChannel(queryClient, teamId, message.channelId, (c) => ({
              lastMessageAt: message.createdAt,
              unreadCount: visible ? 0 : c.unreadCount + 1,
              mentionCount: visible ? 0 : c.mentionCount + (mention ? 1 : 0),
            }));
            if (!visible) {
              alertForMessage(message, {
                meId: me.id,
                channel: conversation,
                members,
                roles,
                open: () => void navigate(`/app/t/${teamId}/c/${message.channelId}`),
              });
            }
          }),
          bindEvent(channel, 'message:updated', (message) =>
            cache.upsertMessage(queryClient, message),
          ),
          bindEvent(channel, 'message:deleted', ({ id, channelId: cid }) =>
            cache.removeMessage(queryClient, cid, (m) => m.id === id),
          ),
          bindEvent(channel, 'meeting:updated', (meeting) =>
            cache.upsertMeeting(queryClient, meeting),
          ),
          bindEvent(channel, 'call:updated', (call) => {
            queryClient.setQueryData<ActiveCall[]>(keys.calls(teamId), (list) => [
              ...(list ?? []).filter((c) => c.channelId !== call.channelId),
              ...(call.participants.length > 0 ? [call] : []),
            ]);
          }),
          bindEvent(channel, 'call:recording', (event) => recordingStore.remote(event)),
          bindEvent(channel, 'call:started', ({ channelId: cid, startedBy }) => {
            if (startedBy.id === me.id || callStore.current()?.join.channelId === cid) return;
            const conversation = channelsRef.current?.find((c) => c.id === cid);
            const where = conversation ? channelLabel(conversation) : t('una conversación');
            const title = t('{{name}} ha iniciado una llamada', { name: startedBy.displayName });
            playSound('call');
            void notify({
              title,
              body: t('En {{where}}. Abre Nexo para unirte.', { where }),
              tag: `call-${cid}`,
              onClick: () => void navigate(`/app/t/${teamId}/c/${cid}`),
            });
            toast({
              title,
              description: t('{{where}} · Pulsa para unirte', {
                where: conversation ? channelLabel(conversation) : t('Conversación'),
              }),
              onClick: () => void callStore.start(cid, false, where),
            });
          }),
          bindEvent(channel, 'typing', (event) => {
            if (event.userId !== me.id) typingStore.add(event);
          }),
          // Tasks from private conversations are only broadcast on the conversation channel.
          bindEvent(channel, 'task:created', (task) => cache.upsertTask(queryClient, task)),
          bindEvent(channel, 'task:updated', (task) => cache.upsertTask(queryClient, task)),
          bindEvent(channel, 'task:deleted', ({ id }) => cache.removeTask(queryClient, teamId, id)),
        ],
      });
    }

    return () => {
      for (const sub of subscriptions) {
        sub.unbind.forEach((fn) => fn());
        client.unsubscribe(sub.name);
      }
    };
  }, [client, channelIds, teamId, me.id, queryClient, activeChannelRef, toast, navigate]);

  return online;
}
